import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CategoryList } from '../components/CategoryList';
import { useCategories } from '../hooks/useCategories';
import { apiService } from '../services/api';
import { Category, Funnel } from '../types';

export function CategoriesPage() {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [labelerToggleId, setLabelerToggleId] = useState<string | null>(null);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const navigate = useNavigate();

  const pageSize = 20;

  const {
    categories,
    total,
    loading,
    error,
    refresh,
    deleteCategory,
  } = useCategories({
    search: search || undefined,
    page: currentPage,
    pageSize,
  });

  useEffect(() => {
    const loadFunnels = async () => {
      try {
        const data = await apiService.getFunnels();
        setFunnels(data);
      } catch (err) {
        console.error('Erro ao carregar funis:', err);
      }
    };

    loadFunnels();
  }, []);

  const funnelNameById = useMemo(() => {
    return funnels.reduce<Record<string, string>>((acc, funnel) => {
      acc[funnel.id] = funnel.nome;
      return acc;
    }, {});
  }, [funnels]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (category: Category) => {
    navigate(`/categorias/${category.id}`);
  };

  const handleNewCategory = () => {
    navigate('/categorias/nova');
  };

  const handleToggleLabelerVisibility = async (category: Category) => {
    try {
      setLabelerToggleId(category.id);

      const nextValue = category.visibleToLabeler === false;
      await apiService.updateCategoryLabelerVisibility(category.id, nextValue);

      toast.success(
        nextValue
          ? 'Categoria visível para o etiquetador'
          : 'Categoria ocultada do etiquetador'
      );

      refresh();
    } catch (error) {
      console.error('Erro ao alternar visibilidade para etiquetador:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar alteração');
    } finally {
      setLabelerToggleId(null);
    }
  };

  return (
    <>
      <Header
        title="Categorias"
        subtitle={`${total} categorias cadastradas`}
        actions={(
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/funis')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium transition-colors"
              aria-label="Gerenciar funis"
            >
              Gerenciar Funis
            </button>
            <button
              onClick={handleNewCategory}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
              aria-label="Nova categoria"
            >
              + Nova Categoria
            </button>
          </div>
        )}
      />

      <div className="p-4 sm:p-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 max-w-lg relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Buscar categorias..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            {search && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 focus:outline-none bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Limpar
              </button>
            )}
          </div>
        </div>

        {error ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Erro ao Carregar</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={refresh}
              className="btn-primary py-3 px-6"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <CategoryList
              categories={categories}
              loading={loading}
              onEdit={handleEdit}
              onDelete={deleteCategory}
              onToggleLabelerVisibility={handleToggleLabelerVisibility}
              labelerToggleId={labelerToggleId}
              funnelNameById={funnelNameById}
            />
          </div>
        )}

        {total > pageSize && (
          <div className="flex justify-center">
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 font-medium transition-colors"
              >
                Anterior
              </button>

              {Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 border rounded-lg font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  style={currentPage === page ? { backgroundColor: 'var(--astra-dark-blue)', borderColor: 'var(--astra-dark-blue)' } : undefined}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(total / pageSize)}
                className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 font-medium transition-colors"
              >
                Próxima
              </button>
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-500">
          Mostrando {categories.length} de {total} categorias (Página {currentPage} de {Math.ceil(total / pageSize)})
        </div>
      </div>

    </>
  );
}
